import GalleryManagerPanel from "./components/GalleryManagerPanel.js";
import HeroManagerPanel from "./components/HeroManagerPanel.js";
import UserManagerPanel from "./components/UserManagerPanel.js";
import GearManagerPanel from "./components/GearManagerPanel.js";
import TagManagerPanel from "./components/TagManagerPanel.js";
import MetricsPanel from "./components/MetricsPanel.js";
import AlertManagerPanel from "./components/AlertManagerPanel.js";
import SecurityPanel from "./components/SecurityPanel.js";

/**
 * The Cockpit's section registry — the single place that decides what the
 * left-hand navigation offers and which panel each entry mounts.
 *
 * Adding a section is one import plus one entry here: build a component that
 * extends CockpitPanel, give it an `id` (which becomes its URL, #/cockpit/:id)
 * and a `labelKey` present in public/i18n/{en,de}.json. Nothing else in the
 * Cockpit needs to change.
 *
 * Four conventions hold the set together (#136):
 *
 * 1. `id`, label and i18n namespace agree. A section with id `tags` is labelled
 *    from `cockpit.nav.tags` and keeps its strings under `cockpit.tags.*`. The
 *    one documented exception is gear, whose API path is /api/showcase — see
 *    CLAUDE.md; the frontend calls it gear everywhere the admin can see.
 * 2. A section that manages a collection is named "<Thing> Manager" /
 *    "<Thing>verwaltung". One that shows something read-only, or holds a single
 *    value, is named for what it shows — which is why Alerts is not "Alert
 *    Manager": it manages nothing, and the German already said Meldungen.
 * 3. `labelKey` doubles as the panel's own heading, so the two can never drift.
 * 4. Every panel implements title/lead/actions/body. `CockpitPanel.empty()` is
 *    for list sections only — Hero and My Account have no list to be empty and
 *    say so in their own files rather than inventing one.
 */

// 20x20, stroke-based so they inherit the nav link's colour on hover/active.
const icon = (paths) =>
  `<svg class="cockpit__nav-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;

const ICONS = {
  galleries: icon(`<rect x="2.5" y="3.5" width="15" height="13" rx="1.5"/><path d="M2.5 12.5 7 8.5l4 3.5 2.5-2 4 3.5"/><circle cx="7" cy="7" r="1.2"/>`),
  hero: icon(`<rect x="2.5" y="3.5" width="15" height="13" rx="1.5"/><path d="M2.5 9.5h15"/><path d="M5.5 6.5h4"/>`),
  users: icon(`<circle cx="7.5" cy="7" r="2.8"/><path d="M2.5 16.5c0-2.8 2.2-4.5 5-4.5s5 1.7 5 4.5"/><path d="M13.5 5.2a2.8 2.8 0 0 1 0 5.4M14.5 12.4c1.9.4 3 1.9 3 4.1"/>`),
  gear: icon(`<rect x="3" y="6" width="14" height="10" rx="2"/><path d="M7.5 6 8.7 4h2.6L12.5 6"/><circle cx="10" cy="11" r="2.8"/>`),
  tags: icon(`<path d="M3.2 9.4V3.5h5.9l7.4 7.4-5.9 5.9-7.4-7.4Z"/><circle cx="6.4" cy="6.6" r="1.1"/>`),
  metrics: icon(`<path d="M3 16.5h14"/><path d="M5.5 16.5v-4M9 16.5V6M12.5 16.5v-7M16 16.5v-3"/>`),
  alerts: icon(`<path d="M10 3.5 2.8 16h14.4L10 3.5Z"/><path d="M10 8v3.5"/><path d="M10 14h.01"/>`),
  security: icon(`<path d="M10 2.8 4 5.2v4.4c0 3.4 2.4 6.4 6 7.6 3.6-1.2 6-4.2 6-7.6V5.2l-6-2.4Z"/><path d="M7.6 10 9.4 11.8 12.8 8.4"/>`),
};

export const COCKPIT_SECTIONS = [
  { id: "galleries", labelKey: "cockpit.nav.galleries", icon: ICONS.galleries, Panel: GalleryManagerPanel },
  { id: "hero", labelKey: "cockpit.nav.hero", icon: ICONS.hero, Panel: HeroManagerPanel },
  { id: "users", labelKey: "cockpit.nav.users", icon: ICONS.users, Panel: UserManagerPanel },
  { id: "gear", labelKey: "cockpit.nav.gear", icon: ICONS.gear, Panel: GearManagerPanel },
  { id: "tags", labelKey: "cockpit.nav.tags", icon: ICONS.tags, Panel: TagManagerPanel },
  { id: "metrics", labelKey: "cockpit.nav.metrics", icon: ICONS.metrics, Panel: MetricsPanel },
  { id: "alerts", labelKey: "cockpit.nav.alerts", icon: ICONS.alerts, Panel: AlertManagerPanel },
  { id: "account", labelKey: "cockpit.nav.account", icon: ICONS.security, Panel: SecurityPanel },
];

export const DEFAULT_SECTION_ID = COCKPIT_SECTIONS[0].id;

export function findSection(id) {
  return COCKPIT_SECTIONS.find((section) => section.id === id) || null;
}
